import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Layout from '../components/Layout';
import { EditorComposer, Editor, Divider } from '../editor';
import ToolbarPlugin from '../editor/plugins/ToolbarPlugin/ToolbarPlugin';
import {
  AlignDropdown,
  BackgroundColorPicker,
  BlockFormatDropdown,
  BoldButton,
  CodeFormatButton,
  CodeLanguageDropdown,
  FloatingLinkEditor,
  FontFamilyDropdown,
  FontSizeDropdown,
  InsertDropdown,
  InsertLinkButton,
  ItalicButton,
  RedoButton,
  TextColorPicker,
  TextFormatDropdown,
  UnderlineButton,
  UndoButton,
} from '../editor/plugins/ToolbarPlugin/components';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createEquationNode } from '../editor/nodes/EquationNode';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { $createImageNode } from '../editor/nodes/ImageNode';
import { 
  processMathEquation, 
  splitTextAndMath, 
  processTableEquation,
  removeMathDelimiters,
  isDisplayMath 
} from '../components/utils/latexParser';
import { Button } from '../components/common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// EditorComponent 수정
function EditorComponent({ onLoad, onSave }) {
  const [editor] = useLexicalComposerContext();

  // 에디터 상태 저장 함수
  const saveEditorState = useCallback(() => {
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    onSave(json);
  }, [editor, onSave]);

  // handleMathData는 그대로 유지
  const handleMathData = useCallback(async (data) => {
    try {
      // console.log("Received data in handleMathData:", data);
      
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        
        let currentParagraph = $createParagraphNode();
        root.append(currentParagraph);

        if (data.mathpix_data?.lined_data) {
          data.mathpix_data.lined_data.forEach(item => {
            if ((item.type !== 'text' && item.type !== 'math' && item.type !== 'table') || 
                item.type === 'chart' || 
                item.type === 'diagram') {
              // 이미지 데이터 처리
              if (item.content && item.content[0] && 
                  (item.content[0].startsWith('data:image') || item.is_base64)) {
                const imageNode = $createImageNode({
                  altText: 'Problem Image',
                  src: item.content[0],
                  width: item.cnt ? Math.abs(item.cnt[2][0] - item.cnt[0][0]) : undefined,
                  height: item.cnt ? Math.abs(item.cnt[2][1] - item.cnt[0][1]) : undefined,
                  maxWidth: 500,
                  showCaption: false
                });
                currentParagraph.append(imageNode);
              }
            } else if (item.type === 'math' || item.type === 'table') {
              const mathContent = item.content[0];
              let equation = removeMathDelimiters(mathContent);
              
              if (item.type === 'table') {
                equation = processTableEquation(equation);
              }
              
              equation = processMathEquation(equation);
              const equationNode = $createEquationNode(equation, !isDisplayMath(mathContent));
              currentParagraph.append(equationNode);
              
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            } else if (item.type === 'text') {
              item.content.forEach(text => {
                const mathSegments = splitTextAndMath(text);
                mathSegments.forEach(mathSegment => {
                  if (mathSegment.type === 'text') {
                    // 일반 텍스트에서만 (숫자) 패턴을 찾아 변환
                    const segments = mathSegment.content.split(/(\(\d+\))/);
                    segments.forEach(segment => {
                      const numberMatch = segment.match(/\((\d+)\)/);
                      if (numberMatch) {
                        const num = parseInt(numberMatch[1]);
                        if (num >= 1 && num <= 5) {
                          const circleNumber = ['①', '②', '③', '④', '⑤'][num - 1];
                          const equationNode = $createEquationNode(circleNumber, true);
                          currentParagraph.append(equationNode);
                        } else {
                          const textNode = $createTextNode(segment);
                          currentParagraph.append(textNode);
                        }
                      } else {
                        const textNode = $createTextNode(segment);
                        currentParagraph.append(textNode);
                      }
                    });
                  } else { // math
                    // 먼저 달러 기호를 제거한 후 수식 처리
                    const cleanEquation = removeMathDelimiters(mathSegment.content);
                    const equation = processMathEquation(cleanEquation);
                    const equationNode = $createEquationNode(equation, !mathSegment.isDisplay);
                    currentParagraph.append(equationNode);
                  }
                });
              });
              
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            }
          });
        }
      });
    } catch (error) {
      console.error('수식 데이터 처리 중 오류 발생:', error);
      console.error('Error details:', error.message);
    }
  }, [editor]);

  useEffect(() => {
    if (onLoad && typeof onLoad === 'function') {
      onLoad(handleMathData);
    }
  }, [onLoad, handleMathData]);

  return (
    <div>
      <div className="editor-container">
        <Editor>
          <ToolbarPlugin>
            <FontFamilyDropdown />
            <FontSizeDropdown />
            <Divider />
            <BoldButton />
            <ItalicButton />
            <UnderlineButton />
            <CodeFormatButton />
            <InsertLinkButton />
            <TextColorPicker />
            <BackgroundColorPicker />
            <TextFormatDropdown />
            <Divider />
            <InsertDropdown />
            <Divider />
            <AlignDropdown />
          </ToolbarPlugin>
        </Editor>
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          onClick={saveEditorState}
          disabled={!editor}
        >
          문제 저장
        </Button>
      </div>
    </div>
  );
}

// 메인 컴포넌트
function ProblemRegister() {
  const { getAccessToken, handleApiError } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [labels, setLabels] = useState('');
  const [problemId, setProblemId] = useState(null);
  const [showOcrButton, setShowOcrButton] = useState(false);
  const mathDataHandlerRef = useRef(null);
  const canvasRef = useRef(null);

  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }
      
      const headers = {
        ...options.headers,
        'Authorization': token,
      };
      
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (response.status === 401) {
        handleApiError(new Error('Invalid or expired token'));
        navigate('/signin');
        return null;
      }

      const data = await response.json();
      
      if (data.status_code === 401) {
        handleApiError(new Error('Invalid or expired token'));
        navigate('/signin');
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);


  const initialState = () => {
    const paragraph = $createParagraphNode();
    const text = $createTextNode('문제를 작성해주세요.');
    paragraph.append(text);
    const root = $getRoot();
    root.append(paragraph);
    root.selectEnd();
  };

  // 이미지 선택 처리
  const handleImageSelect = useCallback((file) => {
    setSelectedImage(file);
    setImageUrl(URL.createObjectURL(file));
    setShowOcrButton(false);
    setProblemId(null);
  }, []);

  // 이미지 제거
  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageUrl(null);
    setLabels('');
    setShowOcrButton(false);
    setProblemId(null);
    
    // 파일 입력 초기화 추가
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);

  // 이미지 업로드
  const handleUpload = useCallback(async () => {
    if (!selectedImage) return;

    setIsLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('labels', labels);

      const uploadResponse = await fetchWithTokenRefresh('/api/problems/image', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': await getAccessToken(),
        }
      });

      if (!uploadResponse || uploadResponse.status_code !== 201) {
        throw new Error('이미지 업로드 실패');
      }

      setProblemId(uploadResponse.data.problem_id);
      setShowOcrButton(true);
      alert('이미지가 성공적으로 업로드되었습니다.');
    } catch (error) {
      console.error('이미지 업로드 중 오류:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedImage, labels, fetchWithTokenRefresh, getAccessToken]);

  // OCR 데이터 처리 함수
  const processOCRData = useCallback((linedData) => {
    let text = '';
    linedData.forEach(line => {
      if (line.type === 'math') {
        // 수식 처리
        const content = line.content[0];
        // \[ \] 형식을 $$ $$ 형식으로 변환
        if (content.includes('\\[') && content.includes('\\]')) {
          text += content.replace(/\\\[/g, '$$').replace(/\\\]/g, '$$');
        } else if (content.startsWith('$$')) {
          text += content;  // 이미 $$ 형식이면 그대로 사용
        } else {
          text += content;  // $ 형식이면 그대로 사용
        }
      } else if (line.type === 'text') {
        // 일반 텍스트 처리
        text += line.content.join(' ');
      }
      text += '\n';  // 각 라인 끝에 줄바꿈 추가
    });
    return { text };  // handleMathData가 기대하는 형식로 반환
  }, []);

  // 좌표에 테두리를 그리는 함수
  const drawBoundingBoxes = useCallback((data) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // 이미지 그리기
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      // 각 항목의 테두리 그리기
      if (data?.mathpix_data?.lined_data) {
        data.mathpix_data.lined_data.forEach(item => {
          if (item.cnt && item.cnt.length === 4) {
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            
            const [[x1, y1], [x2, y2], [x3, y3], [x4, y4]] = item.cnt;
            
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.lineTo(x3, y3);
            ctx.lineTo(x4, y4);
            ctx.closePath();
            ctx.stroke();
          }
        });
      }
    };
  }, [imageUrl]);

  // OCR 응답을 받았을 때 테두리 그리기
  const handleOcr = useCallback(async () => {
    if (!problemId || !mathDataHandlerRef.current) return;

    setIsLoading(true);
    setError(null);
    try {
      const ocrResponse = await fetchWithTokenRefresh(`/api/problems/${problemId}/ocr`, {
        method: 'POST',
        body: JSON.stringify({
          mathpix_data: true
        })
      });

      if (!ocrResponse || ocrResponse.status_code !== 201) {
        throw new Error('OCR 처리 실패');
      }

      // 테두리 그리기
      drawBoundingBoxes(ocrResponse.data);
      
      // 기존 데이터 처리
      await mathDataHandlerRef.current(ocrResponse.data);
      
      alert('OCR 처리가 완료되었습니다.');
    } catch (error) {
      console.error('OCR 처리 중 오류:', error);
      alert('OCR 처리에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [problemId, fetchWithTokenRefresh, drawBoundingBoxes]);

  // 클립보드 붙여넣기 핸들러 (에디터 영역 제외)
  const handlePaste = useCallback((event) => {
    // 에디터 영역에서 붙여넣기하면 무시 (에디터가 자체 처리하도록)
    const target = event.target;
    const isEditorArea = target.closest('.editor-wrapper') || 
                        target.closest('.ContentEditable__root') || 
                        target.closest('[contenteditable="true"]');
    
    if (isEditorArea) {
      return; // 에디터 영역에서는 페이지의 붙여넣기 처리하지 않음
    }
    
    const items = event.clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        event.preventDefault();
        const blob = item.getAsFile();
        
        if (blob) {
          // 파일명 생성 (현재 시간 기반)
          const now = new Date();
          const fileName = `pasted-image-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.png`;
          
          // File 객체 생성 (File input과 동일한 형태로)
          const file = new File([blob], fileName, { type: blob.type });
          
          handleImageSelect(file);
        }
        break;
      }
    }
  }, [handleImageSelect]);

  // 드래그 앤 드롭 핸들러
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleImageSelect(file);
      } else {
        alert('이미지 파일만 업로드할 수 있습니다.');
      }
    }
  }, [handleImageSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
  }, []);

  // 이미지 업로드 핸들러
  const handleImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  }, [handleImageSelect]);

  // 이벤트 리스너 등록
  useEffect(() => {
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  // ProblemRegister 컴포넌트 내에 추가
  const setMathDataHandler = useCallback((handler) => {
    mathDataHandlerRef.current = handler;
  }, []);

  // 에디터 데이터 저장 함수
  const handleEditorSave = useCallback(async (lexicalJson) => {
    if (!problemId) return;

    try {
      const response = await fetchWithTokenRefresh(`/api/problems/${problemId}/ocr/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          lexical_json: lexicalJson
        })
      });

      if (!response || (response.status_code !== 200 && response.status_code !== 201)) {
        throw new Error('에디터 데이터 저장 실패');
      }

      console.log('Editor data saved successfully');
      alert('문제가 성공적으로 저장되었습니다.');
      navigate('/problem-edit');
    } catch (error) {
      console.error('에디터 데이터 저장 중 오류:', error);
      alert('문제 저장에 실패했습니다.');
    }
  }, [problemId, fetchWithTokenRefresh]);

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
          <nav className="mb-6">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/problem-edit" className="hover:text-primary">문제관리</Link>
              <span className="mx-2">&gt;</span>
              <Link to="/problem-register" className="hover:text-primary">문제등록</Link>
            </div>
          </nav>
          
          <div>
            {isLoading && (
              <div className="mb-4 text-blue-500">처리 중...</div>
            )}
            {error && (
              <div className="mb-4 text-red-500">{error}</div>
            )}
            <div className="mb-4 space-y-4">
              {/* 파일 선택 버튼 */}
              <div className="mb-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="mb-2 block w-full text-sm text-gray-500
                            file:mr-4 file:rounded-full file:border-0
                            file:bg-blue-50 file:px-4
                            file:py-2 file:text-sm
                            file:font-semibold file:text-blue-700
                            hover:file:bg-blue-100"
                />
              </div>
              
              {/* 드래그 앤 드롭 영역 */}
              <div 
                className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
              >
                <div className="space-y-2">
                  <div className="text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600">
                    여기에 이미지를 <span className="font-medium text-blue-600">드래그 앤 드롭</span> 하세요
                  </div>
                  <div className="text-xs text-gray-500">
                    또는 <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Ctrl+V</kbd>로 클립보드에서 붙여넣기
                    <br />
                    (에디터 외부에서만 동작)
                  </div>
                  <div className="text-xs text-gray-400">
                    PNG, JPG, GIF 파일만 업로드 가능
                  </div>
                </div>
              </div>
              
              {imageUrl && (
                <div className="space-y-4">
                  {/* 이미지 컨테이너 */}
                  <div className="relative overflow-auto rounded-lg border border-gray-200 bg-gray-50">
                    <div className="relative inline-block min-w-full p-4">
                      <img 
                        src={imageUrl} 
                        alt="Selected" 
                        className="size-auto max-w-full"
                      />
                      <canvas
                        ref={canvasRef}
                        className="pointer-events-none absolute left-0 top-0"
                      />
                      <Button
                        onClick={handleRemoveImage}
                        variant="secondary"
                        size="sm"
                        className="absolute right-2 top-2 bg-red-500 text-white hover:bg-red-600"
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      라벨 (쉼표로 구분)
                    </label>
                    <input
                      type="text"
                      value={labels}
                      onChange={(e) => setLabels(e.target.value)}
                      className="mt-1 block w-full rounded-3xl border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                      placeholder="예: 수학, 대단원 등"
                    />
                  </div>

                  <div className="flex space-x-4">
                    {!showOcrButton ? (
                      <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={isLoading}
                      >
                        이미지 업로드
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="primary"
                          onClick={handleOcr}
                          disabled={isLoading}
                          className="bg-success hover:bg-green-600"
                        >
                          OCR 처리 시작
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="[&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
              <EditorComposer initialEditorState={initialState}>
                <EditorComponent 
                  onLoad={setMathDataHandler} 
                  onSave={handleEditorSave}
                />
              </EditorComposer>
            </div>
          </div>
        </div>
    </Layout>
  );
}

export default ProblemRegister;